import { useCallback, useEffect, useState } from "react"

import { CloseOutlined } from "@ant-design/icons"
import { App, Badge, Divider, Dropdown, Flex, List, Space, Tabs, Tag, Typography } from "antd"
import { useTranslation } from "react-i18next"
import ReactJson from "react-json-view"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"

import Button from "../../../../Components/Button"
import Icon from "../../../../Icon"
import ArrowRound from "../../../../Icon/img/arrowRound"
import CheckMarkblue from "../../../../Icon/img/checkMarkblue"
import ThreeDots from "../../../../Icon/img/threeDots"
import useColorJsonKeys, { getJsonViewTheme } from "../../../User/hooks/useColorJsonKeys"
import Creators from "../reducer"
import { getEndpointStatusView } from "../utils"
import { ActionModal } from "./Modals/actionsModals"

import "./endpointDetails.less"

const { Text } = Typography

const items = [
  {
    key: "all",
    label: "All"
  },
  {
    key: "succeeded",
    label: "Succeeded"
  },
  {
    key: "failed",
    label: "Failed"
  }
]

const itemsMenu = [
  {
    key: "activate",
    label: "Activate"
  },
  {
    key: "disable",
    label: "Disable"
  },
  {
    key: "delete",
    label: "Delete"
  }
]

// const event_types_list = [
//   {id: 'evt_3SA4fSDJ7F4Tew2L0oMfy9pv', type: 'charge.succeeded', date: '2022-11-15'},
//   {id: 2, type: 'charge.failed', date: '2022-12-15'},
//   {id: 3, type: 'charge.succeeded', date: '2022-13-15'},
//   {id: 4, type: 'charge.succeeded', date: '2022-14-15'},
// ]

// const event_type_details_json =
//   {
//     "id": "evt_3SA4fSDJ7F4Tew2L0oMfy9pv",
//     "object": "event",
//     "api_version": "2022-11-15",
//     "created": 1758528051,
//     "data": {
//         "object": {
//               "id": "ch_3SA4fSDJ7F4Tew2l0sLlZ9vp",
//               "object": "charge",
//                   "amount": 100,
//                       "application": "ca_00w2X6H1Mk28dhI5E0TnxHtNicjhge01",
//                       "billing_details": {
//                           "address": {
//                                   "city": "Porto Valtravaglia",
//                           },
//                         },
//                 },
//       },
//   }

const EndpointDetailsPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const { endpoint_id } = useParams()

  const notificationConfig = {
    closeIcon: <Text>{t("Undo")}</Text>,
    style: { minWidth: "max-content" }
  }
  //hook for custom changing color of some key's values in react-json-view
  useColorJsonKeys({
    id: "#DF7000"
  })

  const { webhooksFetching, endpointData: endpointDataRaw } = useSelector(
    (state) => state.publicApi
  )
  const isDark = useSelector((state) => state.nav?.theme) === "dark"
  const endpointData = endpointDataRaw?.endpoint || endpointDataRaw || {}
  const eventLogs = endpointData?.event_logs || []
  const endpointEvents = endpointData?.event_types || []
  const listData = eventLogs.length ? eventLogs : endpointEvents
  const statusView = getEndpointStatusView({
    status: endpointData?.status,
    error_rate: endpointData?.error_rate
  })
  const activeEventTypes = endpointEvents
    .filter((type) => type?.is_active !== false)
    .map((type) => type?.event_type_display || type?.event_type)
    .filter(Boolean)
    .join(", ")

  // const pageSize = 10;

  // const [pageNumber, setPageNumber] = useState(1);
  // const [ordering, setOrdering] = useState(null);

  const [activeTab, setActiveTab] = useState("all")
  const [activeTypeData, setActiveTypeData] = useState(null)
  const [modalType, setModalType] = useState(null)

  const updateData = useCallback(() => {
    const logStatus = activeTab === "succeeded" ? "sent" : activeTab === "failed" ? "failed" : null

    const data = {
      log_status: logStatus,
      endpoint_id
    }

    dispatch(Creators.getEndpointRequest({ ...data }))
  }, [activeTab, endpoint_id])

  const onModalActionSuccess = useCallback(() => {
    if (modalType === "activate") {
      notification.success({
        message: t("Endpoint has been activated."),
        ...notificationConfig
      })
    }
    if (modalType === "disable") {
      notification.success({
        message: t("Endpoint was disabled successfully"),
        ...notificationConfig
      })
    }
    if (modalType === "delete") {
      notification.success({
        message: t("Endpoint was deleted successfully"),
        ...notificationConfig
      })
      navigate("/developers/webhooks")
    } else {
      updateData()
    }
    setModalType(null)
  }, [modalType])

  const onResendSuccess = () => {
    notification.success({
      message: t("Event resend has been scheduled."),
      ...notificationConfig
    })
  }

  const handleModalAction = () => {
    if (modalType === "delete") {
      dispatch(Creators.deleteEndpointRequest({ endpoint_id, onSuccess: onModalActionSuccess }))
    } else {
      dispatch(
        Creators.changeEndpointStatusRequest({
          endpoint_id,
          action: modalType,
          onSuccess: onModalActionSuccess
        })
      )
    }
  }

  const isActiveEventDelivered =
    !!activeTypeData?.is_successful ||
    ["sent", "succeeded"].includes((activeTypeData?.status || "").toLowerCase())

  const handleResend = () => {
    if (!eventLogs?.length || !activeTypeData?.id || isActiveEventDelivered) return

    dispatch(
      Creators.resendEventRequest({
        endpoint_id,
        log_id: activeTypeData?.id,
        onSuccess: onResendSuccess
      })
    )
  }

  useEffect(() => {
    updateData()
  }, [updateData])

  useEffect(() => {
    setActiveTypeData(listData?.[0] || null)
  }, [listData])

  return (
    <div className="endpoint-details-page">
      <div className="header" style={{ marginBottom: 23 }}>
        <Flex
          gap={6}
          style={{ cursor: "pointer" }}
          // onClick={()=> navigate('/settings/webhooks', { state: {fromEndpointDetails: true}}) }
          onClick={() => navigate("/developers/webhooks")}
        >
          <Icon type="arrow_left2" />
          <Text type="secondary">{t("Go back")}</Text>
        </Flex>
        <Flex justify="space-between">
          <Text style={{ display: "block", fontWeight: 600, fontSize: 18, margin: "10px 0 4px" }}>
            {endpointData?.callback_url}
          </Text>

          <div>
            <Dropdown
              overlayClassName="custom-menu"
              getPopupContainer={() => document.getElementById("main-layout")}
              menu={{
                items: itemsMenu
                  .filter((item) =>
                    (endpointData?.status || "").toLowerCase() === "disabled"
                      ? item.key !== "disable"
                      : item.key !== "activate"
                  )
                  .map((item) => ({ ...item, label: t(item.label) })),
                onClick: ({ key }) => setModalType(key)
              }}
              trigger={["click"]}
              placement="bottomLeft"
            >
              <Button className="flex center" icon={<ThreeDots />} />
            </Dropdown>
          </div>
        </Flex>
        {(endpointData?.endpoint_id || endpoint_id) && (
          <Text type="secondary" copyable={{ text: endpointData?.endpoint_id || endpoint_id }}>
            {t("Endpoint ID")}: {endpointData?.endpoint_id || endpoint_id}
          </Text>
        )}

        <Flex gap="middle" align="flex-start" style={{ marginTop: 21 }}>
          {[
            { label: "Status", id: "status" },
            { label: "Event type", id: "event_type" },
            { label: "API version", id: "api_version" }
          ]?.map((el, i, array) => (
            <Flex key={el.id} gap={4} justify="center">
              <Flex vertical gap={4}>
                <Text type="secondary">{el.label}</Text>
                {el.id === "status" ? (
                  <Tag
                    color={statusView.tagColor}
                    key={endpointData[el.id]}
                    style={{ margin: 0 }}
                    className={"simple large"}
                  >
                    <Space>
                      <Badge status={statusView.badgeStatus} color={statusView.badgeColor} />
                      <Text>{statusView.text}</Text>
                    </Space>
                  </Tag>
                ) : (
                  <Text>
                    {el.id === "event_type" ? activeEventTypes || "—" : endpointData[el.id] || "—"}
                  </Text>
                )}
              </Flex>
              {i < array.length - 1 && (
                <Divider
                  type="vertical"
                  style={{
                    height: "56px",
                    margin: "0 24px"
                  }}
                />
              )}
            </Flex>
          ))}
        </Flex>
      </div>
      <Flex gap={12}>
        <Tabs
          activeKey={activeTab}
          items={items.map((item) => ({ ...item, label: t(item.label) }))}
          style={{ width: "100%" }}
          onChange={(key) => setActiveTab(key)}
        />
      </Flex>

      <Flex>
        <div className="left-block">
          <Text type="secondary" style={{ display: "block", margin: "8px 0" }}>
            Today
          </Text>
          <div className="types-list-wrapper">
            <List
              size="small"
              className="custom-list"
              dataSource={listData || []}
              renderItem={(item) => (
                <List.Item
                  key={item?.id}
                  extra={<Text type="secondary">{item?.sent_at || item?.date || "N/A"}</Text>}
                  style={{ display: "flex", alignItems: "center" }}
                  onClick={() => setActiveTypeData(item)}
                  className={item?.id === activeTypeData?.id && "active"}
                >
                  <List.Item.Meta
                    avatar={
                      ["sent", "succeeded"]?.includes(item?.status) ? (
                        <CheckMarkblue color={"#35BD29"} />
                      ) : ["failed"]?.includes(item?.status) ? (
                        <CloseOutlined style={{ color: "#D71313" }} />
                      ) : null
                    }
                    title={
                      <div style={{ fontWeight: 400 }}>
                        {item?.event_type_display || item?.event_type}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        </div>
        <div className="right-block">
          <Flex justify="space-between">
            <Text style={{ fontSize: 18, fontWeight: 600 }}>
              {activeTypeData?.event_type_display || activeTypeData?.event_type}
            </Text>
            <Button
              className="flex center"
              icon={<ArrowRound />}
              text={t("Resend")}
              loading={webhooksFetching}
              disabled={
                !eventLogs?.length ||
                !activeTypeData?.id ||
                (endpointData?.status || "").toLowerCase() !== "active" ||
                isActiveEventDelivered
              }
              onClick={handleResend}
            />
          </Flex>
          <Text type="secondary">Response</Text>
          <Flex justify="space-between">
            <div>
              <Text type="secondary">HTTP status code</Text>
            </div>
            <div>
              <Text type="secondary">
                {activeTypeData?.response_status || activeTypeData?.status || "—"}
              </Text>
            </div>
          </Flex>
          <div className="json-wrapper">
            <Text type="secondary">firstEntryJson</Text>
          </div>
          <div className="json-wrapper">
            <ReactJson
              // src={event_type_details_json || {}}
              src={activeTypeData || {}}
              name={null}
              displayArrayKey={false}
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={false}
              collapsed={false}
              theme={getJsonViewTheme(isDark)}
              // showComma={true}
            />
          </div>
        </div>
      </Flex>

      {["activate", "disable", "delete"].includes(modalType) && (
        <ActionModal
          modalType={modalType}
          close={() => setModalType(null)}
          onOk={handleModalAction}
        />
      )}
    </div>
  )
}

export default EndpointDetailsPage
